import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Scale, ArrowLeft, Building2, Users, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Business = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // 'intro', 'quick', 'professional'

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <button type="button" onClick={() => navigate('/chat')} className="w-9 h-9 flex items-center justify-center hover:bg-slate-100 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-slate-900">Bedriftsløsning</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          
          {step === 'intro' && (
            <div className="text-center mb-12">
              <div className="w-20 h-20 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-10 h-10 text-blue-600" />
              </div>
              
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                Juridisk hjelp til bedrifter
              </h1>
              <p className="text-xl text-slate-600 mb-12">
                Hvordan vil du fortsette?
              </p>

              <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                
                {/* Quick Option */}
                <button
                  onClick={() => setStep('quick')}
                  className="bg-white border-2 border-slate-200 hover:border-blue-500 rounded-2xl p-8 text-left transition-all hover:shadow-xl group"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Enkelt ærend</h3>
                  <p className="text-slate-600 mb-4">
                    Én juridisk vurdering for bedriften
                  </p>
                  <div className="text-3xl font-bold text-blue-600 mb-2">499 kr</div>
                  <p className="text-sm text-slate-500">
                    Betaler per sak • Ingen abonnement
                  </p>
                </button>

                {/* Professional Option */}
                <button
                  onClick={() => setStep('professional')}
                  className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-300 hover:border-purple-500 rounded-2xl p-8 text-left transition-all hover:shadow-xl group"
                >
                  <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">Professional</h3>
                  <p className="text-slate-600 mb-4">
                    Løpende juridisk støtte med teamtilgang
                  </p>
                  <div className="text-3xl font-bold text-purple-600 mb-2">Fra 2.999 kr</div>
                  <p className="text-sm text-slate-500">
                    Per måned • Ubegrenset saker
                  </p>
                </button>
              </div>
            </div>
          )}

          {step === 'quick' && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white border border-slate-200 rounded-3xl p-10 shadow-lg">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Send inn bedriftssak</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Firmanavn</label>
                    <Input placeholder="ACME AS" className="h-12" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Organisasjonsnummer</label>
                    <Input placeholder="123 456 789" className="h-12" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Kontaktperson</label>
                    <Input placeholder="Ditt navn" className="h-12" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">E-post</label>
                    <Input type="email" placeholder="din@bedrift.no" className="h-12" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-900 mb-2">Beskriv saken</label>
                    <textarea
                      className="w-full h-32 p-4 border border-slate-300 rounded-xl resize-none"
                      placeholder="Hva gjelder saken?"
                    />
                  </div>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 h-14 text-lg font-bold rounded-xl">
                    Send til advokat (499 kr)
                  </Button>
                </div>
              </div>
            </div>
          )}

          {step === 'professional' && (
            <div className="max-w-2xl mx-auto text-center">
              <div className="bg-white border border-slate-200 rounded-3xl p-10 shadow-lg">
                <div className="w-16 h-16 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">
                  Professional Bedriftsløsning
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  Løpende juridisk støtte for hele teamet
                </p>
                
                <div className="text-left space-y-4 mb-8">
                  {[
                    'Ubegrenset juridiske vurderinger',
                    'Team-tilgang for ansatte',
                    'Prioritert behandling',
                    'Dedikert kontaktperson',
                    'Månedsrapporter',
                    'Integrasjon med deres systemer'
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-purple-600" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-purple-50 rounded-2xl p-6 mb-8">
                  <p className="text-sm text-purple-900 mb-2">Pris tilpasses bedriftens størrelse</p>
                  <div className="text-4xl font-bold text-purple-600">Fra 2.999 kr/mnd</div>
                </div>
                
                <Button className="w-full bg-purple-600 hover:bg-purple-700 h-14 text-lg font-bold rounded-xl mb-4">
                  Kontakt oss for tilbud
                </Button>
                
                <button
                  onClick={() => setStep('intro')}
                  className="text-slate-600 hover:text-slate-900 underline text-sm"
                >
                  ← Tilbake
                </button>
              </div>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default Business;
